const { canonicalize, sha256Hex } = require('./crypto');

const MODE_PLANNING = 'planning';
const MODE_OPERATIONAL = 'operational';

function createSeedCommitPayload(result, createdAt = new Date().toISOString()) {
  return {
    createdAt,
    method: result.algorithmId,
    seed: result.seed,
    normalizedParameters: result.parameters,
    randFlowVersion: result.version
  };
}

async function createSeedCommit(result, createdAt = new Date().toISOString()) {
  const payload = createSeedCommitPayload(result, createdAt);
  const hash = await sha256Hex(canonicalize(payload));

  return {
    type: 'randFlow seed commit',
    algorithm: 'SHA-256',
    createdAt,
    hash,
    payload,
    externalRegistrationInstruction: 'Register this hash and timestamp outside randFlow before enrollment, for example by institutional email, OSF, ClinicalTrials.gov, or an approved study repository.'
  };
}

function createOperationalSession(result, options = {}) {
  return {
    mode: MODE_OPERATIONAL,
    startedAt: options.startedAt || new Date().toISOString(),
    revealedCount: Number.isInteger(options.revealedCount) ? options.revealedCount : 0,
    total: result.sequence.length,
    closedAt: null
  };
}

function revealNextAllocation(session, result) {
  if (!session || session.mode !== MODE_OPERATIONAL) {
    throw new Error('Operational session is not active');
  }

  if (session.revealedCount >= result.sequence.length) {
    return {
      session,
      allocation: null
    };
  }

  const index = session.revealedCount;
  const allocation = result.sequence[index];

  return {
    session: {
      ...session,
      revealedCount: index + 1
    },
    allocation
  };
}

function getOperationalRows(result, revealedCount, limit = result.sequence.length) {
  return result.sequence.slice(0, limit).map((patient, index) => {
    const isRevealed = index < revealedCount;

    return {
      ...patient,
      revealStatus: isRevealed ? 'revealed' : 'concealed',
      arm: isRevealed ? patient.arm : '',
      armIndex: isRevealed ? patient.armIndex : null
    };
  });
}

function buildOperationalExport(result, signature, session) {
  return {
    ...result,
    experimentSignature: signature,
    operationalMode: {
      active: true,
      revealedCount: session.revealedCount,
      concealedCount: Math.max(0, result.sequence.length - session.revealedCount),
      startedAt: session.startedAt,
      closedAt: session.closedAt
    },
    sequence: getOperationalRows(result, session.revealedCount)
  };
}

function closeOperationalSession(session) {
  if (!session || session.mode !== MODE_OPERATIONAL) {
    return session;
  }

  return {
    ...session,
    closedAt: new Date().toISOString()
  };
}

module.exports = {
  MODE_OPERATIONAL,
  MODE_PLANNING,
  buildOperationalExport,
  closeOperationalSession,
  createOperationalSession,
  createSeedCommit,
  createSeedCommitPayload,
  getOperationalRows,
  revealNextAllocation
};
